import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  DEFAULT_CATEGORY_ID,
  type BlueprintSection,
  type DefinitionDataSection,
  type LocalizedLabel,
} from '../../../types';
import { useDefinitionContext } from '../../contexts/DefinitionContext';
import { AvailableColumnCard } from './AvailableColumnCard';

interface AvailableColumnsListProps {
  blueprintSection: BlueprintSection;
  definitionSection: DefinitionDataSection;
  onAddColumn: (columnId: string) => void;
}

export function AvailableColumnsList({
  blueprintSection,
  definitionSection,
  onAddColumn,
}: AvailableColumnsListProps) {
  const { languages } = useDefinitionContext();
  const primaryLanguage = languages[0] || 'en';

  // Get selected column IDs
  const selectedColumnIds = new Set(definitionSection.columns.map((c) => c.id));

  // Filter out already selected columns
  const availableColumns = blueprintSection.columns.filter((col) => !selectedColumnIds.has(col.id));

  // Group by category
  const columnsByCategory = availableColumns.reduce(
    (acc, col) => {
      const category = col.category || DEFAULT_CATEGORY_ID;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(col);
      return acc;
    },
    {} as Record<string, typeof availableColumns>,
  );

  if (availableColumns.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage
            id="audit.reportDefinitions.availableColumns.empty"
            defaultMessage="No hay columnas disponibles"
          />
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {Object.entries(columnsByCategory).map(([categoryId, columns]) => {
        const category = blueprintSection.categories?.find((c) => c.id === categoryId);

        if (!category) {
          return null;
        }

        const label = category.label[primaryLanguage];

        return (
          <Box key={categoryId} marginBottom={2}>
            {category.id !== DEFAULT_CATEGORY_ID && (
              <Typography variant="subtitle2" gutterBottom>
                {label}
              </Typography>
            )}
            {columns.map((column) => (
              <AvailableColumnCard
                key={column.id}
                column={column}
                categoryLabel={label}
                columnLabel={(column.defaultLabel as LocalizedLabel)[primaryLanguage] || column.key}
                onAddColumn={onAddColumn}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
